import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

function ThreeD() {
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const animationIdRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // Prevent double mounting - clean up any existing canvas
    if (mount.children.length > 0) {
      mount.innerHTML = '';
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x202020);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      75,
      mount.clientWidth / mount.clientHeight,
      0.1,
      1000
    );
    camera.position.z = 3;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    rendererRef.current = renderer;
    
    const width = mount.clientWidth;
    const height = mount.clientHeight;
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    
    mount.appendChild(renderer.domElement);

    // === Lights ===
    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0xffffff, 1);
    pointLight.position.set(5, 5, 5);
    scene.add(pointLight);

    let headModel = null;
    let baseY = 0;
    const loader = new GLTFLoader();
    loader.load(
      "/models/male_head.glb",
      (gltf) => {
        headModel = gltf.scene;

        // Apply wireframe material
        headModel.traverse((child) => {
          if (child.isMesh) {
            child.material = new THREE.MeshBasicMaterial({
              color: 0x00ffcc,
              wireframe: true,
            });
          }
        });

        // Scale down to fit
        headModel.scale.set(0.4, 0.4, 0.4);

        // Center the model
        const box = new THREE.Box3().setFromObject(headModel);
        const center = box.getCenter(new THREE.Vector3());
        headModel.position.sub(center);
        baseY = headModel.position.y;

        scene.add(headModel);
      },
      undefined,
      (error) => {
        console.error("Error loading model:", error);
      }
    );

    // === Animation Loop ===
    let time = 0;
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      time += 0.01;

      if (headModel) {
        // Continuous rotation around Y axis
        headModel.rotation.y += 0.005;

        // Floating bob effect (up and down)
        headModel.position.y = baseY + Math.sin(time) * 0.15;
      }

      renderer.render(scene, camera);
    };
    animate();

    // === Handle Resize ===
    const handleResize = () => {
      if (mount && rendererRef.current) {
        const width = mount.clientWidth;
        const height = mount.clientHeight;
        
        rendererRef.current.setSize(width, height);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      }
    };
    window.addEventListener("resize", handleResize);

    // === Cleanup ===
    return () => {
      window.removeEventListener("resize", handleResize);
      
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      
      // Clean up scene
      if (sceneRef.current) {
        sceneRef.current.traverse((object) => {
          if (object.geometry) {
            object.geometry.dispose();
          }
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
      }
      
      if (mount) {
        mount.innerHTML = '';
      }
    };
  }, []); // Empty dependency array

  return (
    <div
      ref={mountRef}
      style={{ width: "100%", height: "100%", overflow: "hidden" }}
    />
  );
}

export default ThreeD;